import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntensityAdjustment = ({ 
  adjustment, 
  onConfirm, 
  onCancel,
  isVisible = false 
}) => {
  if (!isVisible || !adjustment) return null;

  const getAdjustmentIcon = () => {
    switch (adjustment?.type) {
      case 'increase': return 'TrendingUp';
      case 'decrease': return 'TrendingDown';
      case 'maintain': return 'Minus';
      default: return 'Activity';
    }
  };

  const getAdjustmentColor = () => {
    switch (adjustment?.type) {
      case 'increase': return 'text-success bg-success/10 border-success/20';
      case 'decrease': return 'text-warning bg-warning/10 border-warning/20';
      case 'maintain': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-foreground bg-muted border-border';
    }
  };

  const getAdjustmentMessage = () => {
    switch (adjustment?.type) {
      case 'increase':
        return `Great performance! Let's increase the intensity to level ${adjustment?.newIntensity}.`;
      case 'decrease':
        return `Let's adjust the intensity down to level ${adjustment?.newIntensity} for better form.`;
      case 'maintain':
        return `Perfect! We'll maintain the current intensity level ${adjustment?.newIntensity}.`;
      default:
        return 'Intensity adjustment recommended.';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <div className="bg-surface rounded-2xl p-8 shadow-2xl border border-border max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className={`w-24 h-24 rounded-full ${getAdjustmentColor()} flex items-center justify-center mx-auto mb-6`}>
            <Icon name={getAdjustmentIcon()} size={48} />
          </div>
          
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Intensity Adjustment
          </h2>
          
          <p className="text-xl text-muted-foreground mb-6">
            {getAdjustmentMessage()}
          </p>
        </div>

        {/* Adjustment Details */}
        <div className="bg-accent rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <p className="text-lg text-muted-foreground mb-2">Current</p>
              <div className="flex items-center justify-center space-x-2">
                <p className="text-2xl font-bold text-foreground">
                  {adjustment?.currentWeight} {adjustment?.unit}
                </p>
                <span className="text-lg text-muted-foreground">
                  (Level {adjustment?.currentIntensity})
                </span>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-lg text-muted-foreground mb-2">New</p>
              <div className="flex items-center justify-center space-x-2">
                <p className="text-2xl font-bold text-primary">
                  {adjustment?.newWeight} {adjustment?.unit}
                </p>
                <span className="text-lg text-primary">
                  (Level {adjustment?.newIntensity})
                </span>
              </div>
            </div>
          </div>

          {/* Change Indicator */}
          <div className="flex items-center justify-center mt-6 pt-6 border-t border-border">
            <div className="flex items-center space-x-3">
              <Icon name={getAdjustmentIcon()} size={24} className={adjustment?.type === 'increase' ? 'text-success' : adjustment?.type === 'decrease' ? 'text-warning' : 'text-primary'} />
              <span className="text-lg font-semibold text-foreground">
                {adjustment?.type === 'increase' ? '+' : adjustment?.type === 'decrease' ? '-' : ''}
                {Math.abs(adjustment?.newWeight - adjustment?.currentWeight)} {adjustment?.unit}
              </span>
            </div>
          </div>
        </div>

        {/* Reasoning */}
        <div className="bg-muted/30 rounded-xl p-6 mb-8">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={24} className="text-primary mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Why this adjustment?
              </h3>
              <p className="text-base text-muted-foreground">
                {adjustment?.reasoning || "Based on your performance in the previous sets, this adjustment will help optimize your training."}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            size="xl"
            onClick={onCancel}
            className="flex-1 h-16 text-xl breathing-focus haptic-button"
          >
            <Icon name="X" size={24} className="mr-3" />
            Keep Current
          </Button>
          
          <Button
            variant="default"
            size="xl"
            onClick={onConfirm}
            className="flex-1 h-16 text-xl breathing-focus haptic-button"
          >
            <Icon name="Check" size={24} className="mr-3" />
            Apply Adjustment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntensityAdjustment;